import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Accordion, ButtonGroup, Dropdown, ListGroup } from "react-bootstrap";
import { faCog, faEye, faTimes } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../@ui/IconButton";
import { useBoundarySelectContext } from "./BoundarySelectContext";
import { BoundarySource } from "../../constant/BoundarySource";
import { useBoundaryService } from "../../hooks";
import WKT from "ol/format/WKT";
import olGeometry from "ol/geom/Geometry";
import { BoundarySelection } from "../../types/Boundary/BoundarySelection";
import { useAuthContext } from "../auth/AuthContext";
import { useLocation } from "react-router-dom";

const MapOverlay = styled.div`
  position: absolute;
  right: 37px;
  top: 10px;
  width: 250px;
  z-index: 1000;
`;

const BoundaryList = () => {
  const {
    boundaries,
    removeBoundary,
    selectBoundary,
    deselectBoundary,
    setConfigBoundaryId,
    setViewExtent,
  } = useBoundarySelectContext();
  const [expanded, setExpanded] = useState(false);
  const boundaryService = useBoundaryService();
  useEffect(() => {
    if (!expanded && !!boundaries.length) {
      setExpanded(true);
    }
  }, [boundaries]);
  const wktFormater = useMemo(() => new WKT(), []);

  const zoomToBoundary = (boundary: BoundarySelection) => {
    var extent = (boundary.geometry as olGeometry).getExtent();
    setViewExtent([...extent]);
  };

  const authContext = useAuthContext();
  const isLoggedIn = !!authContext.user;
  const location = useLocation();

  const downloadBoundaries = (projection: number) => {
    boundaryService
      .DownloadBoundaries({
        withinBoundary: boundaries
          .filter((b) => b.source === BoundarySource.KnownBoundary)
          .map((b) =>
            (b.bufferDistanceMetres ?? 0) > 0 ?
              `${b.boundaryId.toString()} ${b.bufferDistanceMetres}`
            : b.boundaryId.toString(),
          ),
        wkt: boundaries
          .filter((b) => b.source !== BoundarySource.KnownBoundary)
          .map((b) => ({
            wkt: `SRID=3857;${wktFormater.writeGeometry(b.geometry)}`,
            name: b.name,
          })),
        projection,
      })
      .then((response) => {
        const dataUrl = window.URL.createObjectURL(response.data);
        const tempLink = document.createElement("a");
        tempLink.href = dataUrl;
        tempLink.setAttribute("download", response.fileName);
        tempLink.click();
        tempLink.remove();
      })
      .catch(() => {});
  };

  if (location.pathname !== "/") {
    return null;
  }

  return (
    <MapOverlay>
      <Accordion
        activeKey={expanded ? "boundaries" : undefined}
        onSelect={() => setExpanded(!expanded)}
      >
        <Accordion.Item eventKey="boundaries">
          <Accordion.Header>Selected Areas</Accordion.Header>
          <Accordion.Body className="p-0">
            <ListGroup
              variant="flush"
              style={{ maxHeight: "300px", overflow: "auto" }}
            >
              {boundaries.map((boundary) => (
                <ListGroup.Item
                  key={boundary.boundaryId}
                  className="d-flex justify-content-between align-items-center"
                  onMouseEnter={() => selectBoundary(boundary.boundaryId)}
                  onMouseLeave={() => deselectBoundary(boundary.boundaryId)}
                >
                  <span>{boundary.name} </span>
                  <ButtonGroup className="ps-2">
                    <IconButton
                      className="btn-sm"
                      title="Zoom to Boundary"
                      icon={faEye}
                      onClick={() => zoomToBoundary(boundary)}
                    />
                    <IconButton
                      className="btn-sm"
                      title="Configure"
                      icon={faCog}
                      onClick={() => setConfigBoundaryId(boundary.boundaryId)}
                    />
                    <IconButton
                      className="btn-sm btn-danger"
                      title="Delete"
                      icon={faTimes}
                      onClick={() => removeBoundary(boundary.boundaryId)}
                    />
                  </ButtonGroup>
                </ListGroup.Item>
              ))}
            </ListGroup>
            {!!boundaries.length && (
              <div className="px-1 py-1 justify-content-center d-flex">
                <Dropdown>
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    Download areas as shapefile
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => downloadBoundaries(4326)}>
                      As WGS84
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => downloadBoundaries(27700)}>
                      As BNG
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
            {boundaries.length === 0 && (
              <ListGroup.Item className="p-2">No Selected Areas</ListGroup.Item>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </MapOverlay>
  );
};

export default BoundaryList;
